// Whether a scene may be turned into a motion scene.
//
// Motionizing takes a scene's narration and its images and returns ONE animated
// document that replaces the picture for the whole scene. That only makes sense
// when the picture is nothing but those images: an avatar, a quiz, a chart or a
// text box on the same scene would be covered by the generated document and
// silently lost, and the user would have no way to tell what happened.
//
// So the rule is narrow on purpose — narration plus images, nothing else — and
// the tab is hidden rather than left to fail later.
//
// A scene that already holds a MotionScene is blocked too. Not because
// regenerating is dangerous, but because at that point the way to change it is
// the editor's chat ("make the orange paler at 17s"), which edits the scene in
// place instead of throwing it away and paying for a new one.

const IMAGE_PLUGIN_IDS = new Set(["BasicImage", "HotspotImages", "HotspotsImage"]);
// Narration is the other half of the input, so a sound box belongs here.
const SOUND_PLUGIN_IDS = new Set(["AudioCue"]);

export const MOTION_ALLOWED_PLUGIN_IDS = new Set([...IMAGE_PLUGIN_IDS, ...SOUND_PLUGIN_IDS]);

/** Human-facing names for the things that block, so the reason can be shown. */
const BLOCKER_LABELS = {
  AvatarPlayer: "avatar",
  Character: "karakter",
  Characters: "karakter",
  MotionScene: "motion sahnesi",
  EnrichedPlayer: "video",
  BasicText: "yazı kutusu",
  RichText: "yazı kutusu",
  HtmlContent: "html içerik",
  DataTable: "tablo",
  GraficaD3: "grafik",
  Shapes: "şekil",
  ShapeOverlay: "şekil",
  EnrichedPDF: "PDF",
};

export function blockerLabel(pluginId) {
  return BLOCKER_LABELS[pluginId] || pluginId;
}

/**
 * Inspect one scene's boxes.
 *
 * `present` is the undo-group present slice; `sceneId` the nav item. Reads
 * pluginToolbarsById first because it is authoritative for plugin identity —
 * boxesById.state can lag, and an AudioCue box in particular often has no
 * pluginId there at all.
 *
 * Returns { eligible, imageCount, blockers } where blockers is a de-duplicated
 * list of plugin ids that disqualify the scene, in the order they were found.
 */
export function motionSceneEligibility(present, sceneId) {
  const out = { eligible: false, imageCount: 0, blockers: [] };
  if (!present || !sceneId) return out;

  const boxes = present.boxesById || {};
  const toolbars = present.pluginToolbarsById || {};
  const seen = new Set();

  Object.keys(boxes).forEach((id) => {
    const box = boxes[id];
    if (!box || box.parent !== sceneId) return;
    const pluginId = toolbars[id]?.pluginId || box.pluginId;
    // A box with no identifiable plugin is a container or a layout artefact,
    // not content; counting it would block every scene.
    if (!pluginId) return;

    if (IMAGE_PLUGIN_IDS.has(pluginId)) {
      out.imageCount += 1;
      return;
    }
    if (SOUND_PLUGIN_IDS.has(pluginId)) return;

    if (!seen.has(pluginId)) {
      seen.add(pluginId);
      out.blockers.push(pluginId);
    }
  });

  out.eligible = out.blockers.length === 0;
  return out;
}
